import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable,
  pointerWithin, closestCenter, defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { JobTile, SortableJobTile } from './routebuilder/JobTile';
import DraftsPanel from './routebuilder/DraftsPanel';
import FinalizeDialog from './routebuilder/FinalizeDialog';
import { ensureGoogleMaps, onMapsAuthFailure, mapsKeyProblemText } from '../services/mapsKeyService';
import {
  listOpenDrafts, fetchRoutesForDate, fetchFullLoads, createDraft, setDraftLoads, updateDraftMeta,
  discardDraft, suggestOrder, draftTimeline, previewFinalize, finalizeDraft,
} from '../services/routeDraftService';
import {
  toggleInSelection, pruneSelection, dragGroup, applySavedOrder, moveItem, insertIntoOrder,
  draftStopOrder, todayLocalISO, STARTED_STATUSES,
} from '../utils/routeBuilderModel';
import { supabase } from '../supabaseClient';
// __JOB_ORDER__ (2026-10-27) each driver's job order (his Trello list / the Driver app), kept by the hub
import { getJobOrder, saveJobOrder } from '../services/hubPlanning';
import { refillByDriver, withDriverOrder } from '../utils/jobOrder';
import { 
  calculateDriverDailyRoute,
  getRoutingConfig,
  calculateRoute,
  reconcileRoutesAfterReassignment,
  guardReassignAllowed,
  simulateSwap,
  stampStopPlan
} from '../services/routingEngineService';
import { decodePolyline } from '../services/mapService';
import { parseDbTime, parseWindowTime } from '../utils/dbTime';
// __EQUIP_COMPAT__ the hard equipment warning (drag onto a driver, Finalize) + driver-chip hints
import { useEquipmentGate, EquipmentIssueList } from './EquipmentGate';
import { checkAssignment, fetchFleet, fetchRules, quickHint, invalidateEquipmentCache, qualifyDrivers } from '../services/equipmentService';
import { timelineTime } from '../services/timingService';

// __ROUTE_DRAFTS__ one select for the job list (initial load + refreshes):
// load_stops(*) brings place_name (business names on tiles) and coordinates.
const LOADS_SELECT = `
  id,
  load_number,
  status,
  driver_id,
  commodity,
  customer_name,
  scheduled_date,
  started_at,
  weight_kg,
  length_cm,
  width_cm,
  height_cm,
  billing_data,
  load_stops (*),
  customer:customers(company_name)
`;
// billing_data: __TRUCK_REQUIRED__ (tarp / closed truck) is read from it by the Finalize Qualifies check

const LIST_ORDER_KEY = 'hmgc_rb_list_order';
const GROUP_KEY = 'hmgc_rb_group_by_route';
const EXPAND_KEY = 'hmgc_rb_expand_routes';
const readPref = (k, fallback) => { try { const v = localStorage.getItem(k); return v == null ? fallback : JSON.parse(v); } catch (e) { return fallback; } };
const writePref = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private window */ } };
const DRAFT_COLOR = '#f1c40f';

// Drop target for a driver chip (drag a job onto a driver = real reassignment,
// same confirm + preview as before).
function DriverDropChip({ driverId, children, style, onClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'driver:' + driverId, data: { type: 'driver', driverId } });
  return (
    <div ref={setNodeRef} onClick={onClick} style={{ ...style, boxShadow: isOver ? '0 0 0 3px var(--accent-primary)' : 'none', transform: isOver ? 'scale(1.05)' : 'none' }}>
      {children}
    </div>
  );
}

// The job list's scroll area: dropping a draft tile here takes it out of the draft.
function JobListDropArea({ children }) {
  const { setNodeRef } = useDroppable({ id: 'joblist', data: { type: 'joblist' } });
  return <div ref={setNodeRef} style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>{children}</div>;
}

// Collision rules: a driver chip under the pointer wins; inside a draft's drop
// zone the nearest tile of THAT draft; inside the job list the nearest list
// tile (so the tiles below slide up even when the pointer is in a gap or past
// the last tile, like Trello); anywhere else nothing.
const collisionStrategy = (args) => {
  const within = pointerWithin(args);
  const drv = within.filter((c) => String(c.id).startsWith('driver:'));
  if (drv.length) return drv;
  const zone = within.find((c) => String(c.id).startsWith('draftzone:'));
  if (zone) {
    const draftId = String(zone.id).slice('draftzone:'.length);
    const items = args.droppableContainers.filter((c) => {
      const id = String(c.id);
      const d = (c.data && c.data.current) || {};
      return id.startsWith('dl:' + draftId + ':') || (id.startsWith('job:') && d.draftId === draftId);
    });
    const hit = items.length ? closestCenter({ ...args, droppableContainers: items }) : [];
    return hit.length ? hit : [zone];
  }
  if (within.some((c) => c.id === 'joblist')) {
    const items = args.droppableContainers.filter((c) => String(c.id).startsWith('job:') && !((c.data && c.data.current) || {}).draftId);
    const hit = items.length ? closestCenter({ ...args, droppableContainers: items }) : [];
    return hit.length ? hit : within.filter((c) => c.id === 'joblist');
  }
  return [];
};

const dropAnimation = {
  duration: 220,
  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.35' } } }),
};

const DRIVER_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
];

function RouteBuilder() {
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [filterMode, setFilterMode] = useState('all');
  const [loading, setLoading] = useState(true);
  const [routeData, setRouteData] = useState({});
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [draggedLoad, setDraggedLoad] = useState(null);
  const [showReassignWarning, setShowReassignWarning] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [googleMapsError, setGoogleMapsError] = useState(false);
  const [isCalculatingRoutes, setIsCalculatingRoutes] = useState(false);
  const [timeViolations, setTimeViolations] = useState([]);
  const [showDriverSelectPrompt, setShowDriverSelectPrompt] = useState(false);
  const [routeBuilderStatus, setRouteBuilderStatus] = useState(null); // { type: 'success'|'error', text }
  const [addressWarnings, setAddressWarnings] = useState([]);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [modifiedStops, setModifiedStops] = useState({});
  const [isDraggingWaypoint, setIsDraggingWaypoint] = useState(false);
  const [waypointDragInfo, setWaypointDragInfo] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const polylinesRef = useRef([]);
  const markersRef = useRef([]);
  const directionsRenderersRef = useRef([]);
  // __ROUTE_DRAFTS__ selection, per-viewer list order, real routes of the day,
  // draft routes, drag state, finalize dialog; __BOARD_MAPS_KEY__ map problem.
  const [routeDay, setRouteDay] = useState(() => todayLocalISO());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [listOrder, setListOrder] = useState(() => readPref(LIST_ORDER_KEY, []));
  const [groupByRoute, setGroupByRoute] = useState(() => readPref(GROUP_KEY, true));
  const [expandAllRoutes, setExpandAllRoutes] = useState(() => readPref(EXPAND_KEY, false));
  const [expandedRoutes, setExpandedRoutes] = useState(() => new Set());
  const [dayRoutes, setDayRoutes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftsError, setDraftsError] = useState(null);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [draftTimelines, setDraftTimelines] = useState({});
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [finalizeFor, setFinalizeFor] = useState(null);
  const [mapsProblem, setMapsProblem] = useState(null);
  const timelineReq = useRef(0);
  // __JOB_ORDER__ every driver's job order from the hub: each driver's tiles in his order (the order of his Trello list
  // and the Driver app); dragging one of his tiles above / below another of his = his new order for everyone.
  const [jobOrder, setJobOrder] = useState(null);
  const fetchJobOrder = useCallback(async () => {
    const r = await getJobOrder();
    if (r.ok) setJobOrder({ drivers: r.drivers || {}, routes: r.routes || {} });
  }, []);
  useEffect(() => {
    fetchJobOrder();
    const iv = setInterval(fetchJobOrder, 15000);
    return () => clearInterval(iv);
  }, [fetchJobOrder]);
  const [gateEquipment, equipmentModal] = useEquipmentGate();
  const [equipFleet, setEquipFleet] = useState(null);
  const [equipRules, setEquipRules] = useState(null);
  const refreshEquipment = useCallback(async () => {
    invalidateEquipmentCache();
    try { const [f, r] = await Promise.all([fetchFleet(true), fetchRules(true)]); setEquipFleet(f); setEquipRules(r); }
    catch (e) { console.warn('equipment read failed:', e); }
  }, []);
  useEffect(() => { refreshEquipment(); }, [refreshEquipment]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchData();
  }, []);

  // __ROUTE_DRAFTS__ real routes for the chosen day + the open drafts
  const refreshDayRoutes = useCallback(async (day) => {
    try { setDayRoutes(await fetchRoutesForDate(day)); }
    catch (e) { console.warn('routes for day failed:', e); setDayRoutes([]); }
  }, []);
  const refreshDrafts = useCallback(async () => {
    try { setDrafts(await listOpenDrafts()); setDraftsError(null); }
    catch (e) { console.warn('drafts failed:', e); setDraftsError(e.message || String(e)); }
  }, []);
  useEffect(() => { refreshDayRoutes(routeDay); }, [routeDay, refreshDayRoutes]);
  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);
  useEffect(() => { writePref(GROUP_KEY, groupByRoute); }, [groupByRoute]);
  useEffect(() => { writePref(EXPAND_KEY, expandAllRoutes); }, [expandAllRoutes]);
  useEffect(() => { writePref(LIST_ORDER_KEY, listOrder.slice(-2000)); }, [listOrder]);
  useEffect(() => { setSelectedIds((prev) => pruneSelection(prev, loads.map((l) => l.id))); }, [loads]);
  useEffect(() => {
    if (activeDraftId && !drafts.some((d) => d.id === activeDraftId)) setActiveDraftId(drafts.length ? drafts[0].id : null);
    if (!activeDraftId && drafts.length) setActiveDraftId(drafts[0].id);
  }, [drafts, activeDraftId]);

  // __BOARD_MAPS_KEY__ the key comes from the shared board setting (falls back
  // to this login's own). A missing or refused key shows plain words instead of
  // Google's grey "This page can't load Google Maps correctly" box.
  useEffect(() => {
    let alive = true;
    const off = onMapsAuthFailure(() => {
      if (!alive) return;
      setMapsProblem('AUTH_FAILED');
      setGoogleMapsError(true);
      setGoogleMapsLoaded(false);
      setMapInstance(null);
    });
    ensureGoogleMaps()
      .then(() => { if (alive) { setGoogleMapsLoaded(true); setGoogleMapsError(false); setMapsProblem(null); } })
      .catch((error) => {
        if (!alive) return;
        console.warn('Google Maps not available:', error && error.message);
        setMapsProblem((error && error.code) || 'LOAD_FAILED');
        setGoogleMapsLoaded(false);
        if (!error || error.code !== 'NO_KEY') setGoogleMapsError(true);
      });
    return () => { alive = false; off(); };
  }, []);
// Callback ref: initializes map when the container div appears in the DOM
  const mapCallbackRef = useCallback((node) => {
    if (!node) return; // div removed from DOM
    mapContainerRef.current = node;
    
    // If map already exists on this node, skip
    if (mapInstance) return;
    
    // Check if Google Maps API is available
    if (!window.google?.maps?.Map) return;
    
    try {
      const config = getRoutingConfig();
      const centerLat = config.homeLat || 43.6532;
      const centerLng = config.homeLng || -79.3832;
      const zoomLevel = config.homeRadius ? Math.max(6, Math.min(14, Math.round(14 - Math.log2(config.homeRadius / 10)))) : 10;
      const map = new window.google.maps.Map(node, {
        center: { lat: centerLat, lng: centerLng },
        zoom: zoomLevel,
        mapTypeId: 'roadmap',
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
          { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
        ],
      });
      setMapInstance(map);
      setGoogleMapsLoaded(true);
    } catch (error) {
      console.error('Failed to initialize Google Maps:', error);
      setGoogleMapsError(true);
    }
  }, [mapInstance]);

  // Reset map on unmount
  useEffect(() => {
    return () => {
      setMapInstance(null);
    };
  }, []);

  const getDriverColor = (driverId) => {
    if (String(driverId).startsWith('draft:')) return DRAFT_COLOR;
    const index = drivers.findIndex(d => d.id === driverId);
    return DRIVER_COLORS[index % DRIVER_COLORS.length];
  };

  const getDriverName = (driverId) => {
    if (String(driverId).startsWith('draft:')) {
      const dr = drafts.find((x) => x.id === String(driverId).slice(6));
      return 'Draft: ' + (dr ? dr.name : 'route');
    }
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || driver?.email || 'Unknown';
  };

  // __ROUTE_DRAFTS__ what the map draws: the selected drivers' routes plus the
  // open draft's timeline (yellow, markers not draggable - a draft is not real).
  const mapRouteKeys = useMemo(() => {
    const keys = [...selectedDrivers];
    const t = activeDraftId ? draftTimelines[activeDraftId] : null;
    if (t && t.success) keys.push('draft:' + activeDraftId);
    return keys;
  }, [selectedDrivers, activeDraftId, draftTimelines]);
  const routeFor = useCallback((key) => (String(key).startsWith('draft:')
    ? draftTimelines[String(key).slice(6)]
    : routeData[key]), [draftTimelines, routeData]);

  const clearMapOverlays = useCallback(() => {
    polylinesRef.current.forEach(polyline => polyline.setMap(null));
    polylinesRef.current = [];
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    directionsRenderersRef.current.forEach(r => r.setMap(null));
    directionsRenderersRef.current = [];
  }, []);

  const handleWaypointDragStart = useCallback((driverId, stopId, stopIndex) => {
    setIsDraggingWaypoint(true);
    setWaypointDragInfo({ driverId, stopId, stopIndex });
  }, []);

  const handleWaypointDragEnd = useCallback(async (driverId, stopId, stopIndex, newPosition) => {
    setIsDraggingWaypoint(false);
    
    const newLat = newPosition.lat();
    const newLng = newPosition.lng();
    
    setModifiedStops(prev => ({
      ...prev,
      [stopId]: {
        latitude: newLat,
        longitude: newLng,
        originalStopId: stopId,
        driverId,
        stopIndex,
        isModified: true
      }
    }));

    setRouteData(prev => {
      const driverRoute = prev[driverId];
      if (!driverRoute?.stopsWithETAs) return prev;

      const updatedStops = driverRoute.stopsWithETAs.map((stop, idx) => {
        if (idx === stopIndex) {
          return {
            ...stop,
            latitude: newLat,
            longitude: newLng,
            isManuallyMoved: true
          };
        }
        return stop;
      });

      return {
        ...prev,
        [driverId]: {
          ...driverRoute,
          stopsWithETAs: updatedStops,
          needsRecalculation: true
        }
      };
    });

    setWaypointDragInfo(null);
  }, []);

  const recalculateRouteForDriver = useCallback(async (driverId) => {
    const driverRoute = routeData[driverId];
    if (!driverRoute?.stopsWithETAs || driverRoute.stopsWithETAs.length < 2) return;

    setIsCalculatingRoutes(true);

    const stops = driverRoute.stopsWithETAs.filter(s => s.latitude && s.longitude);
    const stopsForRoute = stops.map(s => ({
      address: s.address,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude)
    }));

    try {
      const routeResult = await calculateRoute(stopsForRoute, {
        optimize: false,
        departureTime: new Date(),
        trafficModel: 'best_guess'
      });

      if (routeResult.success) {
        const violations = [];
        let currentTime = new Date();
        
        stops.forEach((stop, index) => {
          if (index > 0 && routeResult.legs && routeResult.legs[index - 1]) {
            const leg = routeResult.legs[index - 1];
            currentTime = new Date(currentTime.getTime() + (leg.durationInTrafficSeconds || leg.durationSeconds) * 1000);
          }
          
          if (stop.time_window_end) {
            // currentTime is built from new Date() + real leg durations, so it is a
            // true instant. __TZ_WINDOWS__ time_window_end is Toronto wall clock:
            // parseWindowTime makes it the real instant (DST-safe).
            const windowEnd = parseWindowTime(stop.time_window_end);
            if (windowEnd && currentTime > windowEnd) {
              const lateMinutes = Math.round((currentTime - windowEnd) / 60000);
              violations.push({
                driverId,
                driverName: getDriverName(driverId),
                stop,
                lateBy: lateMinutes,
                message: `${lateMinutes} min late`
              });
            }
          }
          
          currentTime = new Date(currentTime.getTime() + 30 * 60000);
        });

        if (violations.length > 0) {
          setTimeViolations(prev => {
            const filtered = prev.filter(v => v.driverId !== driverId);
            return [...filtered, ...violations];
          });
        } else {
          setTimeViolations(prev => prev.filter(v => v.driverId !== driverId));
        }
      }
    } catch (error) {
      console.error('Error recalculating route:', error);
    }

    setIsCalculatingRoutes(false);
  }, [routeData, getDriverName]);

const renderRoutesOnMap = useCallback(async () => {
    if (!mapInstance || !googleMapsLoaded) return;
    clearMapOverlays();
    
    // Clear previous DirectionsRenderers
    directionsRenderersRef.current.forEach(r => r.setMap(null));
    directionsRenderersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidBounds = false;

    for (const driverId of mapRouteKeys) {
      const route = routeFor(driverId);
      if (!route?.stopsWithETAs || route.stopsWithETAs.length < 2) continue;
      const isDraftRoute = String(driverId).startsWith('draft:');

      const color = getDriverColor(driverId);
      const stops = route.stopsWithETAs.filter(s => s.latitude && s.longitude);
      if (stops.length < 2) continue;

      // Use DirectionsService to get and render the route
      try {
        const directionsService = new window.google.maps.DirectionsService();
        const origin = stops[0].address || `${stops[0].latitude},${stops[0].longitude}`;
        const destination = stops[stops.length - 1].address || `${stops[stops.length - 1].latitude},${stops[stops.length - 1].longitude}`;
        const waypoints = stops.slice(1, -1).map(s => ({
          location: s.address || `${s.latitude},${s.longitude}`,
          stopover: true,
        }));

        const result = await new Promise((resolve, reject) => {
          directionsService.route({
            origin,
            destination,
            waypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode.DRIVING,
          }, (response, status) => {
            if (status === 'OK') resolve(response);
            else reject(new Error(status));
          });
        });

        // Render the route line on the map
        const renderer = new window.google.maps.DirectionsRenderer({
          map: mapInstance,
          directions: result,
          suppressMarkers: true, // We draw our own markers
          polylineOptions: {
            strokeColor: color,
            strokeOpacity: 0.9,
            strokeWeight: 5,
          },
        });
        directionsRenderersRef.current.push(renderer);

        // Extend bounds from the route
        result.routes[0].legs.forEach(leg => {
          bounds.extend(leg.start_location);
          bounds.extend(leg.end_location);
          hasValidBounds = true;
        });

      } catch (error) {
        console.error('Error rendering route for driver:', error);
        // Fallback: draw straight lines between stops
        const path = stops.map(s => new window.google.maps.LatLng(parseFloat(s.latitude), parseFloat(s.longitude)));
        const polyline = new window.google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor: color,
          strokeOpacity: 0.7,
          strokeWeight: 4,
          map: mapInstance,
        });
        polylinesRef.current.push(polyline);
        path.forEach(point => {
          bounds.extend(point);
          hasValidBounds = true;
        });
      }

      // Draw custom markers for each stop
      stops.forEach((stop, index) => {
        const position = new window.google.maps.LatLng(parseFloat(stop.latitude), parseFloat(stop.longitude));
        const isPickup = stop.stop_type === 'pickup';
        const stopId = stop.id || `${driverId}-${index}`;
        const isModified = stop.isManuallyMoved || modifiedStops[stopId];

        const marker = new window.google.maps.Marker({
          position,
          map: mapInstance,
          draggable: !isDraftRoute,
          title: `${isDraftRoute ? 'Draft - ' : ''}${isPickup ? 'Pickup' : 'Delivery'}: ${stop.place_name ? stop.place_name + ' - ' : ''}${stop.address}${isModified ? ' (Modified)' : ''}${isDraftRoute ? '' : '\nDrag to adjust location'}`,
          icon: {
            path: isPickup ? window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW : window.google.maps.SymbolPath.CIRCLE,
            scale: isModified ? 10 : 8,
            fillColor: isModified ? '#f1c40f' : color,
            fillOpacity: 1,
            strokeColor: isModified ? '#e74c3c' : '#ffffff',
            strokeWeight: isModified ? 3 : 2,
          },
          label: {
            text: String(index + 1),
            color: '#ffffff',
            fontSize: '10px',
            fontWeight: 'bold',
          },
          cursor: 'grab',
        });

        marker.addListener('dragstart', () => {
          handleWaypointDragStart(driverId, stopId, index);
          marker.setOptions({ cursor: 'grabbing' });
        });

        marker.addListener('dragend', () => {
          const newPosition = marker.getPosition();
          handleWaypointDragEnd(driverId, stopId, index, newPosition);
          marker.setOptions({ cursor: 'grab' });
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="color: #333; padding: 8px;">
              <strong>${isPickup ? 'Pickup' : 'Delivery'} #${index + 1}</strong>${isModified ? ' <span style="color: #e74c3c;">(Modified)</span>' : ''}<br/>
              <span>${stop.address}</span><br/>
              ${stop.arrivalFormatted ? `<span>ETA: ${stop.arrivalFormatted}</span><br/>` : ''}
              ${stop.dwellMinutes != null ? `<span style="font-size: 11px; color: #555;">Stop time: ${stop.dwellMinutes} min${stop.dwellReason ? ` (${String(stop.dwellReason).replace(/[<>&]/g, '')})` : ''}</span><br/>` : ''}
              ${stop.isWithinTimeWindow ? `<span style="color: ${stop.isWithinTimeWindow.status === 'late' ? 'red' : 'green'}">${stop.isWithinTimeWindow.message}</span><br/>` : ''}
              <span style="font-size: 11px; color: #666;">Drag marker to adjust location</span>
            </div>
          `,
        });

        marker.addListener('click', () => {
          infoWindow.open(mapInstance, marker);
        });

        markersRef.current.push(marker);
        bounds.extend(position);
        hasValidBounds = true;
      });
    }

    if (hasValidBounds) {
      mapInstance.fitBounds(bounds, { padding: 50 });
    }
  }, [mapInstance, googleMapsLoaded, mapRouteKeys, routeFor, clearMapOverlays, modifiedStops, handleWaypointDragStart, handleWaypointDragEnd]);

  // Recalculation only happens on "Build Routes" (or a draft change); selecting
  // drivers just draws what is already computed.
  useEffect(() => {
    if (mapInstance && mapRouteKeys.some((k) => routeFor(k))) {
      renderRoutesOnMap();
    } else if (mapInstance) {
      clearMapOverlays();
    }
  }, [mapInstance, mapRouteKeys, routeFor, renderRoutesOnMap, clearMapOverlays]);

  useEffect(() => {
    const driversNeedingRecalc = selectedDrivers.filter(driverId => {
      const route = routeData[driverId];
      return route?.needsRecalculation;
    });

    if (driversNeedingRecalc.length > 0) {
      const recalcTimeout = setTimeout(() => {
        driversNeedingRecalc.forEach(driverId => {
          recalculateRouteForDriver(driverId);
        });
        
        setRouteData(prev => {
          const updated = { ...prev };
          driversNeedingRecalc.forEach(driverId => {
            if (updated[driverId]) {
              updated[driverId] = { ...updated[driverId], needsRecalculation: false };
            }
          });
          return updated;
        });
      }, 500);

      return () => clearTimeout(recalcTimeout);
    }
  }, [routeData, selectedDrivers, recalculateRouteForDriver]);

  const resetModifiedStops = useCallback(() => {
    setModifiedStops({});
    calculateRoutes();
  }, []);

  const [isSavingWaypoints, setIsSavingWaypoints] = useState(false);

  const saveModifiedWaypoints = useCallback(async () => {
    const modifiedStopIds = Object.keys(modifiedStops);
    if (modifiedStopIds.length === 0) return;

    setIsSavingWaypoints(true);

    try {
      // __JOB_CHANGES__ the stops of each job as they were, so the hub can tell the driver a pin moved
      const beforeByLoad = {};
      try {
        const { data: before } = await supabase.from('load_stops').select('*').in('id', modifiedStopIds);
        for (const r of before || []) { (beforeByLoad[r.load_id] = beforeByLoad[r.load_id] || []).push(r); }
      } catch (e) { /* not reported then */ }
      const updates = modifiedStopIds.map(stopId => {
        const modification = modifiedStops[stopId];
        return supabase
          .from('load_stops')
          .update({
            latitude: modification.latitude,
            longitude: modification.longitude
          })
          .eq('id', stopId);
      });

      const results = await Promise.all(updates);
      
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Some waypoint updates failed:', errors);
        setRouteBuilderStatus({ type: 'error', text: `${errors.length} waypoint(s) failed to save. Please try again.` });
      } else {
        setModifiedStops({});
        setRouteBuilderStatus({ type: 'success', text: `${modifiedStopIds.length} waypoint location(s) saved.` });
        try {   // __JOB_CHANGES__ per job: its whole stop list as it was (the moved ones from before, the rest as they are)
          const { reportJobChanged } = await import('../services/hubPlanning');
          for (const lid of Object.keys(beforeByLoad)) {
            const { data: all } = await supabase.from('load_stops').select('*').eq('load_id', lid);
            const was = (all || []).map((s) => (beforeByLoad[lid].find((b) => b.id === s.id) || s));
            reportJobChanged(lid, {}, was, 'Route Builder pins');
          }
        } catch (e) { console.warn('job change not reported:', e); }
        await calculateRoutes();
      }
    } catch (error) {
      console.error('Error saving waypoint modifications:', error);
      setRouteBuilderStatus({ type: 'error', text: 'Failed to save waypoints: ' + error.message });
    }

    setIsSavingWaypoints(false);
  }, [modifiedStops]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use Promise.allSettled to show partial results faster
      // Reduced timeout to 10 seconds for better UX
      const withTimeout = (promise, ms = 10000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), ms)
          )
        ]);
      };

      // Fetch drivers first (smaller query, faster response)
      // Only select needed fields - use name directly from drivers table
      const driversPromise = withTimeout(
        supabase
          .from('drivers')
          .select('id, name, email, is_available')
      );

      // Fetch loads with only needed fields
      // Note: latitude, longitude, time_critical are optional columns added by routing engine migration
      // Only request columns that are guaranteed to exist in the base schema
      const loadsPromise = withTimeout(
        supabase
          .from('loads')
          .select(LOADS_SELECT)
          .not('status', 'in', '("complete","cancelled","ready_to_bill","billed","archived")')
          .order('created_at', { ascending: false })
      );

      // Use allSettled so one failure doesn't block the other
      const [driversSettled, loadsSettled] = await Promise.allSettled([driversPromise, loadsPromise]);

      // Process drivers result
      if (driversSettled.status === 'fulfilled' && !driversSettled.value.error) {
        setDrivers(driversSettled.value.data || []);
      } else {
        console.error('Error fetching drivers:', driversSettled.reason || driversSettled.value?.error);
        setDrivers([]);
      }

      // Process loads result
      if (loadsSettled.status === 'fulfilled' && !loadsSettled.value.error) {
        setLoads(loadsSettled.value.data || []);
      } else {
        console.error('Error fetching loads:', loadsSettled.reason || loadsSettled.value?.error);
        setLoads([]);
      }

      // Don't auto-select first driver - let user choose to avoid triggering route calculation on mount
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mirror a computed route into the driver-app tables (routes + route_loads).
  // The driver app reads these; the routing engine's own driver_daily_routes is
  // invisible to it. Idempotent per (driver, planned_date): updates the existing
  // route row and replaces its route_loads, else inserts a new one.
  const persistDriverAppRoute = async (driverId, result) => {
    const date = result.date || new Date().toISOString().split('T')[0];
    // Ordered, de-duplicated load ids in actual driving order (fallback: load list).
    const seq = [];
    (result.stopsWithETAs || []).forEach((stp) => {
      if (stp.loadId && !seq.includes(stp.loadId)) seq.push(stp.loadId);
    });
    if (seq.length === 0) {
      (result.loads || []).forEach((l) => { if (l.id && !seq.includes(l.id)) seq.push(l.id); });
    }
    if (seq.length === 0) return;

    const durationMinutes = result.route && result.route.totalDurationSeconds != null
      ? Math.round(result.route.totalDurationSeconds / 60)
      : null;
    const distanceKm = result.totalDistanceKm != null
      ? Math.round(result.totalDistanceKm * 10) / 10
      : null;
    const routePayload = {
      driver_id: driverId,
      status: 'planned',
      planned_date: date,
      total_stops: (result.stopsWithETAs || []).length || seq.length,
      estimated_distance_km: distanceKm,
      estimated_duration_minutes: durationMinutes,
      updated_at: new Date().toISOString(),
    };

    // routes has no unique (driver, date) constraint, so look up then update/insert.
    const { data: existing } = await supabase
      .from('routes').select('id').eq('driver_id', driverId).eq('planned_date', date).limit(1);

    let routeId;
    if (existing && existing.length > 0) {
      routeId = existing[0].id;
      await supabase.from('routes').update(routePayload).eq('id', routeId);
      await supabase.from('route_loads').delete().eq('route_id', routeId);
    } else {
      routePayload.route_number = 'RT-' + date + '-' + String(driverId).slice(0, 4).toUpperCase();
      const { data: ins, error: insErr } = await supabase
        .from('routes').insert(routePayload).select('id').single();
      if (insErr || !ins) { console.warn('driver-app route insert failed:', insErr); return; }
      routeId = ins.id;
    }
    const rows = seq.map((loadId, i) => ({ route_id: routeId, load_id: loadId, sequence_order: i + 1 }));
    const { error: rlErr } = await supabase.from('route_loads').insert(rows);
    if (rlErr) console.warn('driver-app route_loads insert failed:', rlErr);

    // Stamp each stop's PLAN onto load_stops (planned arrival/departure, stop
    // time + why, drive into it, prep) - the baseline live GPS is compared
    // against. __PLAN_BASELINE__ one writer shared with the routing engine's twin
    // (stampStopPlan); it no longer writes dwell_minutes (the human override).
    await stampStopPlan(result, 'build_routes');
  };

  const calculateRoutes = async () => {
    setIsCalculatingRoutes(true);
    const newRouteData = {};
    const violations = [];
    const allWarnings = [];

    for (const driverId of selectedDrivers) {
      try {
        const result = await calculateDriverDailyRoute(driverId, null);
        if (result.success) {
          newRouteData[driverId] = result;
          // Persist a driver-app route (routes + route_loads) so routed jobs show
          // as a route in the driver app. The routing engine only writes
          // driver_daily_routes, which the phone cannot read. Additive + guarded:
          // a failure here must never break route calculation for the dispatcher.
          try { await persistDriverAppRoute(driverId, result); }
          catch (e) { console.warn('driver-app route persist failed (non-fatal):', e); }
          if (result.failedStops && result.failedStops.length > 0) {
            allWarnings.push(...result.failedStops);
          }
          
          if (result.stopsWithETAs) {
            result.stopsWithETAs.forEach(stop => {
              if (stop.isWithinTimeWindow && stop.isWithinTimeWindow.status === 'late') {
                violations.push({
                  driverId,
                  driverName: getDriverName(driverId),
                  stop,
                  lateBy: stop.isWithinTimeWindow.lateMinutes,
                  message: stop.isWithinTimeWindow.message
                });
              }
            });
          }
        }
      } catch (error) {
        console.error(`Error calculating route for driver ${driverId}:`, error);
      }
    }

    setRouteData(newRouteData);
    setTimeViolations(violations);
    setAddressWarnings(allWarnings);
    updateMapBounds(newRouteData);
    setIsCalculatingRoutes(false);
    // __JOB_ORDER__ the routes just built: the hub makes / updates each one's Trello route card and keeps its jobs together
    Object.keys(newRouteData).forEach((driverId) => { saveDriverJobOrder(driverId, [], 'Route Builder: Build Routes', true); });
  };

  const handleBuildRoutes = () => {
    if (selectedDrivers.length === 0) {
      setShowDriverSelectPrompt(true);
      return;
    }
    if (selectedDrivers.length > 1) {
      setShowDriverSelectPrompt(true);
      return;
    }
    calculateRoutes();
  };

  const selectSingleDriverAndBuildRoute = (driverId) => {
    setSelectedDrivers([driverId]);
    setShowDriverSelectPrompt(false);
  };

  const updateMapBounds = (routes) => {
    const allCoords = [];
    Object.values(routes).forEach(route => {
      if (route.stopsWithETAs) {
        route.stopsWithETAs.forEach(stop => {
          if (stop.latitude && stop.longitude) {
            allCoords.push({ lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) });
          }
        });
      }
    });

    if (allCoords.length > 0) {
      const lats = allCoords.map(c => c.lat);
      const lngs = allCoords.map(c => c.lng);
      setMapBounds({
        north: Math.max(...lats) + 0.1,
        south: Math.min(...lats) - 0.1,
        east: Math.max(...lngs) + 0.1,
        west: Math.min(...lngs) - 0.1
      });
    }
  };

  const getFilteredLoads = () => {
    switch (filterMode) {
      case 'unassigned':
        return loads.filter(load => !load.driver_id);
      case 'assigned':
        return loads.filter(load => load.driver_id);
      default:
        return loads;
    }
  };

  // A job dropped on a driver chip (dnd-kit): the same real reassignment flow as
  // before - compatibility check, dry-run impact preview, then Confirm.
  const requestReassign = async (load, targetDriverId) => {
    const draggedLoad = load;
    setDraggedLoad(load);
    if (!draggedLoad) return;

    const sourceDriverId = draggedLoad.driver_id;
    
    if (sourceDriverId === targetDriverId) {
      setDraggedLoad(null);
      return;
    }

    // __EQUIP_COMPAT__ the target driver's unit vs this job (shown in the popup; Confirm
    // runs the hard warning again with a fresh read)
    const compatibility = await checkAssignment({ driverId: targetDriverId, loads: [draggedLoad] }).catch(() => null);
    
    const impactAnalysis = await analyzeReassignmentImpact(
      draggedLoad,
      sourceDriverId,
      targetDriverId
    );

    setShowReassignWarning({
      load: draggedLoad,
      sourceDriverId,
      targetDriverId,
      compatibility,
      impact: impactAnalysis
    });
  };

  // __DRAG_PREVIEW_REAL__ (2026-09-23) the popup shown when a job is dropped on a
  // driver used guesses -- 45 min per stop, "arrival = last departure + 1 h" --
  // and, worse, ran the route engine WITHOUT dry-run, so merely previewing a drag
  // rewrote both drivers' driver_daily_routes for TODAY (not the job's date).
  // Now it is the same dry-run the Swap Preview panel uses (simulateSwap): both
  // drivers' real day re-planned with and without the job, on the job's own date,
  // nothing written until Confirm. Minutes are the change in each driver's end of
  // day; a violation is a stop that is late after the move but was not before.
  const analyzeReassignmentImpact = async (load, sourceDriverId, targetDriverId) => {
    const impact = { sourceDriver: null, targetDriver: null, criticalViolations: [], simulated: false, error: null };
    const mins = (x, y) => { const p = parseDbTime(x), q = parseDbTime(y); return (p && q) ? Math.round((q - p) / 60000) : null; };
    const stopsOf = (r) => ((r && r.success && r.stopsWithETAs) || []);
    const span = (r) => ((r && r.success) ? mins(r.estimatedStartTime, r.estimatedEndTime) : null);
    const date = load.scheduled_date || new Date().toISOString().split('T')[0];
    const sim = await simulateSwap({ loadId: load.id, sourceDriverId: sourceDriverId || null, targetDriverId, date });
    if (sim.error) { impact.error = sim.error; return impact; }
    impact.simulated = true;
    if (sourceDriverId) {
      const b = sim.before.source, a = sim.after.source;
      const freed = (a && a.success) ? mins(a.estimatedEndTime, b && b.success ? b.estimatedEndTime : null) : span(b);
      impact.sourceDriver = {
        name: getDriverName(sourceDriverId),
        currentStops: stopsOf(b).length,
        newStops: stopsOf(a).length,
        timeSaved: freed == null ? 0 : Math.max(0, freed)
      };
    }
    const tb = sim.before.target, ta = sim.after.target;
    const added = (tb && tb.success) ? mins(tb.estimatedEndTime, ta && ta.success ? ta.estimatedEndTime : null) : span(ta);
    impact.targetDriver = {
      name: getDriverName(targetDriverId),
      currentStops: stopsOf(tb).length,
      newStops: stopsOf(ta).length,
      timeAdded: added == null ? 0 : Math.max(0, added)
    };
    const lateBefore = new Set(stopsOf(tb)
      .filter((st) => st.isWithinTimeWindow && st.isWithinTimeWindow.status === 'late')
      .map((st) => st.id));
    stopsOf(ta).forEach((st) => {
      const w = st.isWithinTimeWindow;
      if (w && w.status === 'late' && !lateBefore.has(st.id)) {
        impact.criticalViolations.push({ stop: st, lateBy: w.lateMinutes || 0 });
      }
    });
    return impact;
  };

  const confirmReassignment = async () => {
    if (!showReassignWarning) return;

    const { load, targetDriverId } = showReassignWarning;

    try {
      const g = await guardReassignAllowed(load.id);
      if (g.tier === 'blocked') { setRouteBuilderStatus({ type: 'error', text: g.reason }); setShowReassignWarning(null); setDraggedLoad(null); return; }
      if (g.tier === 'override' && !window.confirm('WARNING: ' + g.reason + '\n\nOverride and reassign anyway?')) { setShowReassignWarning(null); setDraggedLoad(null); return; }
      // __EQUIP_COMPAT__ hard equipment warning - before anything is written
      const eq = await gateEquipment({ driverId: targetDriverId, loads: [load], date: load.scheduled_date || undefined, context: 'route_builder_drag', title: 'Drag to ' + getDriverName(targetDriverId) });
      if (!eq.proceed) { setShowReassignWarning(null); setDraggedLoad(null); return; }

      const { error } = await supabase
        .from('loads')
        .update({ 
          driver_id: targetDriverId,
          status: 'assigned'
        })
        .eq('id', load.id);

      if (error) throw error;

      try {
        await reconcileRoutesAfterReassignment({ loadId: load.id, oldDriverId: load.driver_id || null, newDriverId: targetDriverId, scheduledDate: load.scheduled_date || null });
      } catch (e) { console.warn('route reconcile failed (non-fatal):', e); }
      try {   // __JOB_CHANGES__ both drivers hear it from the hub (the job's change line + their pushes)
        const { reportJobChanged } = await import('../services/hubPlanning');
        reportJobChanged(load.id, load, null, 'Route Builder');
      } catch (e) { console.warn('job change not reported:', e); }

      const { data: updatedLoads } = await supabase
        .from('loads')
        .select(LOADS_SELECT)
        .not('status', 'in', '("complete","cancelled","ready_to_bill","billed","archived")')
        .order('created_at', { ascending: false });
      if (updatedLoads) setLoads(updatedLoads);
      refreshDayRoutes(routeDay);
      await calculateRoutes();
      setShowReassignWarning(null);
      setDraggedLoad(null);
    } catch (error) {
      console.error('Error reassigning load:', error);
      setRouteBuilderStatus({ type: 'error', text: 'Failed to reassign: ' + error.message });
    }
  };

  const toggleDriverSelection = (driverId) => {
    setSelectedDrivers(prev => {
      if (prev.includes(driverId)) {
        return prev.filter(id => id !== driverId);
      } else {
        return [...prev, driverId];
      }
    });
  };

  // ============================================================ __ROUTE_DRAFTS__
  const loadsById = useMemo(() => {
    const m = {};
    loads.forEach((l) => { m[l.id] = l; });
    return m;
  }, [loads]);
  // __EQUIP_COMPAT__ the selected tiles, for the per-driver equipment badge
  const selectedEquipLoads = useMemo(() => [...selectedIds].map((id) => loadsById[id]).filter(Boolean), [selectedIds, loadsById]);
  const routeOfLoad = useMemo(() => {
    const m = {};
    dayRoutes.forEach((r) => r.loadIds.forEach((id) => { if (!m[id]) m[id] = r; }));
    return m;
  }, [dayRoutes]);
  const draftsOfLoad = useMemo(() => {
    const m = {};
    drafts.forEach((d) => d.loadIds.forEach((id) => { (m[id] = m[id] || []).push(d); }));
    return m;
  }, [drafts]);
  const activeDraft = drafts.find((d) => d.id === activeDraftId) || null;
  const isStarted = (l) => !!(l && (l.started_at || STARTED_STATUSES.includes(String(l.status || '').toLowerCase())));
  const routeLabel = (r) => (r.driver_id ? getDriverName(r.driver_id) : 'Unassigned') + (r.route_number ? ' · ' + r.route_number : '');

  const toggleSelect = (loadId) => setSelectedIds((prev) => toggleInSelection(prev, loadId));

  const tileProps = (load, ctx = {}) => ({
    selected: selectedIds.has(load.id),
    color: load.driver_id ? getDriverColor(load.driver_id) : '#95a5a6',
    driverName: load.driver_id ? getDriverName(load.driver_id) : null,
    routeBadge: routeOfLoad[load.id] ? routeLabel(routeOfLoad[load.id]) : null,
    draftBadges: (draftsOfLoad[load.id] || []).filter((d) => d.id !== ctx.inDraft).map((d) => d.name),
    inProgress: isStarted(load),
    onClick: () => toggleSelect(load.id),
  });

  // The left list: filtered jobs in this viewer's own order; with "group by
  // route", each real route of the day first (its jobs in driving order, shown
  // when expanded), then every other job.
  const listModel = useMemo(() => {
    const filtered = (filterMode === 'unassigned' ? loads.filter((l) => !l.driver_id)
      : filterMode === 'assigned' ? loads.filter((l) => l.driver_id) : loads);
    const own = applySavedOrder(filtered.map((l) => l.id), listOrder);
    const drvOf = {}; filtered.forEach((l) => { drvOf[l.id] = l.driver_id || null; });
    const ids = jobOrder ? refillByDriver(own, (id) => drvOf[id], jobOrder) : own;   // __JOB_ORDER__
    const visible = new Set(ids);
    const groups = [];
    const inRoute = new Set();
    if (groupByRoute) {
      dayRoutes.forEach((r) => {
        const own = r.loadIds.filter((id) => visible.has(id) && !inRoute.has(id));
        if (!own.length) return;
        own.forEach((id) => inRoute.add(id));
        groups.push({ route: r, loadIds: own });
      });
    }
    const others = ids.filter((id) => !inRoute.has(id));
    return { ids, groups, others, total: filtered.length };
  }, [loads, filterMode, listOrder, groupByRoute, dayRoutes, jobOrder]);

  const isRouteOpen = (routeId) => expandAllRoutes || expandedRoutes.has(routeId);
  const toggleRouteOpen = (routeId) => setExpandedRoutes((prev) => toggleInSelection(prev, routeId));

  // dnd ids of the list tiles in screen order (the dragged one leaves the list
  // while it previews inside a draft)
  const listDndIds = () => {
    const out = [];
    listModel.groups.forEach((g) => { if (isRouteOpen(g.route.id)) g.loadIds.forEach((id) => out.push('job:' + id)); });
    listModel.others.forEach((id) => out.push('job:' + id));
    if (dragPreview && dragState && dragState.from === 'list') return out.filter((x) => x !== dragState.activeId);
    return out;
  };

  const draftRenderOrder = (draft) => {
    const base = draft.loadIds.map((id) => 'dl:' + draft.id + ':' + id);
    if (dragPreview && dragState && dragState.from === 'list' && dragPreview.draftId === draft.id) {
      const keep = draft.loadIds.filter((id) => !dragState.group.includes(id)).map((id) => 'dl:' + draft.id + ':' + id);
      const at = Math.max(0, Math.min(dragPreview.index, keep.length));
      return [...keep.slice(0, at), dragState.activeId, ...keep.slice(at)];
    }
    return base;
  };

  const refreshTimeline = async (draft) => {
    const req = ++timelineReq.current;
    if (!draft || !draft.loadIds.length) {
      setDraftTimelines((t) => ({ ...t, [draft ? draft.id : '_']: null }));
      return;
    }
    setTimelineBusy(true);
    try {
      const full = await fetchFullLoads(draft.loadIds);
      const res = await draftTimeline(full, draft.route_date || routeDay, draft.stop_order);
      if (req === timelineReq.current) setDraftTimelines((t) => ({ ...t, [draft.id]: res }));
    } catch (e) {
      console.warn('draft timeline failed:', e);
    } finally {
      if (req === timelineReq.current) setTimelineBusy(false);
    }
  };

  // time the open draft when it is first shown
  useEffect(() => {
    if (activeDraft && draftTimelines[activeDraft.id] === undefined) refreshTimeline(activeDraft);
  }, [activeDraftId, drafts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraftLoads = async (draft, loadIds, stopOrder = null, timeline = undefined) => {
    const next = { ...draft, loadIds, stop_order: stopOrder };
    setDrafts((ds) => ds.map((d) => (d.id === draft.id ? next : d)));
    try {
      await setDraftLoads(draft.id, loadIds, stopOrder);
    } catch (e) {
      setRouteBuilderStatus({ type: 'error', text: 'Draft not saved: ' + (e.message || e) });
      refreshDrafts();
      return;
    }
    if (timeline !== undefined) setDraftTimelines((t) => ({ ...t, [draft.id]: timeline }));
    else refreshTimeline(next);
  };

  const addToDraft = (draftId, ids, index) => {
    const d = drafts.find((x) => x.id === draftId);
    if (!d) return;
    saveDraftLoads(d, insertIntoOrder(d.loadIds, ids, index), null);
  };
  const removeFromDraft = (d, loadId) => saveDraftLoads(d, d.loadIds.filter((x) => x !== loadId), null);

  const nextDraftName = () => {
    let n = drafts.length + 1;
    const names = new Set(drafts.map((d) => d.name));
    while (names.has('Route ' + n)) n += 1;
    return 'Route ' + n;
  };

  const draftsMissingText = (e) => {
    const m = String((e && e.message) || e || '');
    return /route_drafts|relation|does not exist|schema cache/i.test(m)
      ? 'Draft routes are not set up in the database yet (migration route_drafts.sql).'
      : m;
  };

  const handleNewDraft = async () => {
    try {
      const d = await createDraft({ name: nextDraftName(), routeDate: routeDay });
      setDrafts((ds) => [...ds, d]);
      setActiveDraftId(d.id);
    } catch (e) {
      setRouteBuilderStatus({ type: 'error', text: 'Could not create a draft: ' + draftsMissingText(e) });
    }
  };

  const suggestionNote = (sug, n) => 'Suggested order for ' + n + ' job(s)'
    + (sug.matrixSource === 'estimate' ? ' (drive times estimated - Google Maps not available)' : ' (Google drive times with traffic)')
    + '. Drag tiles to change it, then Finalize.'
    + (sug.failedStops && sug.failedStops.length ? ' ' + sug.failedStops.length + ' stop(s) had no map position and were put last.' : '');

  // Build route from the selected tiles: the engine suggests the best order
  // (pickups before their drop-offs, traffic drive times, each job's stop times
  // and time windows) and it opens as a new DRAFT - nothing real changes.
  const buildRouteFromSelection = async () => {
    const ids = listModel.ids.filter((id) => selectedIds.has(id));
    [...selectedIds].forEach((id) => { if (!ids.includes(id) && loadsById[id]) ids.push(id); });
    if (!ids.length) return;
    setDraftBusy(true);
    setRouteBuilderStatus(null);
    try {
      const full = await fetchFullLoads(ids);
      const sug = await suggestOrder(full, routeDay);
      const order = [...sug.loadOrder, ...ids.filter((id) => !sug.loadOrder.includes(id))];
      const d = await createDraft({ name: nextDraftName(), routeDate: routeDay, loadIds: order, stopOrder: sug.stopOrder });
      setDrafts((ds) => [...ds, d]);
      setActiveDraftId(d.id);
      setDraftTimelines((t) => ({ ...t, [d.id]: sug.timeline }));
      setSelectedIds(new Set());
      setRouteBuilderStatus({ type: 'success', text: suggestionNote(sug, ids.length) });
    } catch (e) {
      setRouteBuilderStatus({ type: 'error', text: 'Build route failed: ' + draftsMissingText(e) });
    } finally {
      setDraftBusy(false);
    }
  };

  const handleOptimizeDraft = async (draft) => {
    setDraftBusy(true);
    try {
      const full = await fetchFullLoads(draft.loadIds);
      const sug = await suggestOrder(full, draft.route_date || routeDay);
      const order = [...sug.loadOrder, ...draft.loadIds.filter((id) => !sug.loadOrder.includes(id))];
      await saveDraftLoads(draft, order, sug.stopOrder, sug.timeline);
      setRouteBuilderStatus({ type: 'success', text: suggestionNote(sug, draft.loadIds.length) });
    } catch (e) {
      setRouteBuilderStatus({ type: 'error', text: 'Optimize failed: ' + (e.message || e) });
    } finally {
      setDraftBusy(false);
    }
  };

  const handleRenameDraft = async (draft, name) => {
    setDrafts((ds) => ds.map((d) => (d.id === draft.id ? { ...d, name } : d)));
    try { await updateDraftMeta(draft.id, { name }); } catch (e) { setRouteBuilderStatus({ type: 'error', text: 'Rename failed: ' + e.message }); }
  };
  const handleDraftDate = async (draft, date) => {
    const next = { ...draft, route_date: date || null };
    setDrafts((ds) => ds.map((d) => (d.id === draft.id ? next : d)));
    try { await updateDraftMeta(draft.id, { route_date: date || null }); } catch (e) { setRouteBuilderStatus({ type: 'error', text: 'Date not saved: ' + e.message }); }
    refreshTimeline(next);
  };
  const handleDiscardDraft = async (draft) => {
    if (!window.confirm('Discard draft "' + draft.name + '"? Its jobs stay exactly where they are now.')) return;
    try {
      await discardDraft(draft.id);
      setDrafts((ds) => ds.filter((d) => d.id !== draft.id));
    } catch (e) { setRouteBuilderStatus({ type: 'error', text: 'Discard failed: ' + e.message }); }
  };

  // Copy a real route's jobs (driving order) into a new draft to re-plan them.
  const routeToDraft = async (route) => {
    try {
      const d = await createDraft({ name: 'Re-plan ' + (route.driver_id ? getDriverName(route.driver_id) : 'open route'), routeDate: route.planned_date || routeDay, loadIds: route.loadIds.filter((id) => loadsById[id]) });
      setDrafts((ds) => [...ds, d]);
      setActiveDraftId(d.id);
    } catch (e) {
      setRouteBuilderStatus({ type: 'error', text: 'Could not create a draft: ' + draftsMissingText(e) });
    }
  };

  // __EQUIP_V2__ the draft's timed route for a day (the cached timeline when it is for that day)
  const routeTimelineFor = async (draft, full, date) => {
    const cached = draftTimelines[draft.id];
    if (cached && cached.success && (draft.route_date || routeDay) === date) return cached;
    try { return await draftTimeline(full, date, draft.stop_order); } catch (e) { return null; }
  };

  const handleFinalizeConfirm = async ({ targetDriverId, date, confirmed }) => {
    const draft = finalizeFor;
    const full = await fetchFullLoads(draft.loadIds);
    const stopOrder = draftStopOrder(full, draft.stop_order);
    // __EQUIP_COMPAT__ the whole route on the target driver's unit (peak weight by the
    // draft's stop order) - before anything is written. Cancel = nothing finalized.
    if (targetDriverId) {
      // __EQUIP_V2__ + his day: what he already has that day + this route's own time
      const added = timelineTime(await routeTimelineFor(draft, full, date)) || undefined;
      const eq = await gateEquipment({ driverId: targetDriverId, loads: full, stopIds: stopOrder, date, added, recordFor: draft.loadIds, context: 'route_finalize', title: 'Finalize "' + draft.name + '"' });
      if (!eq.proceed) throw new Error('Not finalized - the equipment warning was cancelled.');
    }
    const res = await finalizeDraft({ draft, loadIds: draft.loadIds, stopOrder, targetDriverId, date, confirmed });
    if (res.errors.includes('needs_confirm')) throw new Error('Tick the box to confirm moving jobs off their current driver.');
    setFinalizeFor(null);
    setDrafts((ds) => ds.filter((d) => d.id !== draft.id));
    const { data: updatedLoads } = await supabase
      .from('loads').select(LOADS_SELECT)
      .not('status', 'in', '("complete","cancelled","ready_to_bill","billed","archived")')
      .order('created_at', { ascending: false });
    if (updatedLoads) setLoads(updatedLoads);
    refreshDayRoutes(routeDay);
    refreshDrafts();
    const who = targetDriverId ? getDriverName(targetDriverId) : 'Unassigned (open route)';
    const skipped = res.skipped.length ? ' ' + res.skipped.length + ' finished job(s) were left alone.' : '';
    const errs = res.errors.filter((x) => x !== 'needs_confirm');
    setRouteBuilderStatus({
      type: errs.length ? 'error' : 'success',
      text: 'Route "' + draft.name + '" finalized: ' + res.moved.length + ' job(s) to ' + who + ' for ' + date + '.' + skipped
        + (errs.length ? ' Problems: ' + errs.join('; ') : ' Any driver who lost a job sees "Job taken off you" on his next sync.'),
    });
    if (targetDriverId && !selectedDrivers.includes(targetDriverId)) setSelectedDrivers((p) => [...p, targetDriverId]);
    // __JOB_ORDER__ the route's jobs together in his order, in the route's order; the hub makes / updates its Trello route card
    if (targetDriverId && res.moved.length) saveDriverJobOrder(targetDriverId, draft.loadIds.filter((id) => res.moved.some((l) => l.id === id)), 'Route Builder: Finalize', true);
  };

  // ---------------------------------------------------------------- drag & drop
  const handleDndStart = (event) => {
    const d = (event.active.data && event.active.data.current) || {};
    if (d.type === 'job') {
      const ordered = listDndIds().map((x) => x.slice(4));
      setDragState({ activeId: String(event.active.id), from: 'list', loadId: d.loadId, group: dragGroup(d.loadId, selectedIds, ordered) });
    } else if (d.type === 'draftItem') {
      setDragState({ activeId: String(event.active.id), from: 'draft', loadId: d.loadId, draftId: d.draftId, group: [d.loadId] });
    }
  };

  const handleDndOver = (event) => {
    if (!dragState || dragState.from !== 'list' || !activeDraft) return;
    const over = event.over;
    if (!over) { if (dragPreview) setDragPreview(null); return; }
    if (String(over.id) === dragState.activeId) return;   // hovering its own preview slot
    const od = (over.data && over.data.current) || {};
    const inDraft = (od.type === 'draftItem' || od.type === 'draftzone') && od.draftId === activeDraft.id;
    if (!inDraft) { if (dragPreview) setDragPreview(null); return; }
    const keep = activeDraft.loadIds.filter((id) => !dragState.group.includes(id));
    let index = keep.length;
    if (od.type === 'draftItem') {
      const oi = keep.indexOf(od.loadId);
      if (oi >= 0) {
        const r = event.active.rect.current && event.active.rect.current.translated;
        const below = r && over.rect ? r.top > over.rect.top + over.rect.height / 2 : false;
        index = oi + (below ? 1 : 0);
      }
    }
    if (!dragPreview || dragPreview.draftId !== activeDraft.id || dragPreview.index !== index) {
      setDragPreview({ draftId: activeDraft.id, index });
    }
  };

  const handleDndCancel = () => { setDragState(null); setDragPreview(null); };

  const handleDndEnd = (event) => {
    const st = dragState;
    const preview = dragPreview;
    setDragState(null);
    setDragPreview(null);
    if (!st) return;
    const over = event.over;
    if (!over) return;
    const overId = String(over.id);
    const od = (over.data && over.data.current) || {};

    if (overId.startsWith('driver:')) {
      if (st.from !== 'list') return;
      if (st.group.length > 1) {
        setRouteBuilderStatus({ type: 'error', text: 'Drop one job on a driver at a time - or put the selected jobs in a draft and finalize it to that driver.' });
        return;
      }
      const load = loadsById[st.loadId];
      if (load) requestReassign(load, overId.slice(7));
      return;
    }

    if (st.from === 'list') {
      if (preview && preview.draftId) { addToDraft(preview.draftId, st.group, preview.index); return; }
      if ((od.type === 'draftzone' || od.type === 'draftItem') && od.draftId) { addToDraft(od.draftId, st.group, null); return; }
      if (overId.startsWith('job:') && overId !== st.activeId) {
        const ids = listModel.ids;
        const from = ids.indexOf(st.loadId);
        const to = ids.indexOf(overId.slice(4));
        if (from >= 0 && to >= 0) {
          const next = moveItem(ids, from, to);
          setListOrder(next);
          // __JOB_ORDER__ one of a driver's jobs dropped among his own: his new job order (Trello + Driver app follow)
          const a = loadsById[st.loadId];
          const b = loadsById[overId.slice(4)];
          if (a && b && a.driver_id && a.driver_id === b.driver_id && st.group.length === 1) {
            saveDriverJobOrder(a.driver_id, next.filter((id) => (loadsById[id] || {}).driver_id === a.driver_id));
          }
        }
      }
      return;
    }

    if (st.from === 'draft') {
      const draft = drafts.find((x) => x.id === st.draftId);
      if (!draft) return;
      if (od.type === 'draftItem' && od.draftId === st.draftId) {
        const from = draft.loadIds.indexOf(st.loadId);
        const to = draft.loadIds.indexOf(od.loadId);
        if (from >= 0 && to >= 0 && from !== to) saveDraftLoads(draft, moveItem(draft.loadIds, from, to), null);
        return;
      }
      if (od.type === 'draftzone') return;
      if (overId === 'joblist' || overId.startsWith('job:')) removeFromDraft(draft, st.loadId);
    }
  };

  // __JOB_ORDER__ a driver's new job order -> the hub (which moves his Trello cards and tells his phone)
  const saveDriverJobOrder = async (driverId, ids, via = 'Route Builder', routesChanged = false) => {
    if (ids && ids.length) setJobOrder((o) => withDriverOrder(o, driverId, ids));
    const r = await saveJobOrder(driverId, ids || [], via, routesChanged);
    if (!r.ok) {
      setRouteBuilderStatus({ type: 'error', text: 'Job order not saved for ' + getDriverName(driverId) + ': ' + r.message });
      fetchJobOrder();
      return;
    }
    if (ids && ids.length && r.changed) {
      setRouteBuilderStatus({ type: 'success', text: getDriverName(driverId) + "'s job order saved" + (r.trello === 'moving' ? ' - the Trello cards follow in a few seconds' : '') + '.' });
    }
    fetchJobOrder();
  };

  const renderDragOverlay = () => {
    if (!dragState) return null;
    const load = loadsById[dragState.loadId];
    if (!load) return null;
    return (
      <div style={{ width: '280px' }}>
        <JobTile load={load} {...tileProps(load)} overlay groupCount={dragState.group.length} />
      </div>
    );
  };

  const renderDraftsColumn = () => {
    if (draftsError) {
      return (
        <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '0.4rem' }}>Draft routes</div>
          {draftsMissingText(draftsError)}
        </div>
      );
    }
    return (
      <DraftsPanel
        drafts={drafts}
        activeDraftId={activeDraftId}
        onSelectDraft={setActiveDraftId}
        renderOrder={draftRenderOrder}
        loadsById={loadsById}
        tileProps={tileProps}
        timeline={activeDraft ? draftTimelines[activeDraft.id] : null}
        timelineBusy={timelineBusy}
        busy={draftBusy}
        onNewDraft={handleNewDraft}
        onRename={handleRenameDraft}
        onChangeDate={handleDraftDate}
        onOptimize={handleOptimizeDraft}
        onFinalize={(d) => setFinalizeFor(d)}
        onDiscard={handleDiscardDraft}
        onRemoveLoad={removeFromDraft}
      />
    );
  };

  const renderMap = () => {
    const config = getRoutingConfig();
    // __BOARD_MAPS_KEY__ only once the script really loaded with an accepted key
    const isGoogleMapsEnabled = googleMapsLoaded && !googleMapsError && !!window.google?.maps?.Map;

    if (isGoogleMapsEnabled) {
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'var(--bg-primary)' }}>
          <div
            ref={mapCallbackRef}
            style={{
              width: '100%',
              height: '100%',
            }}
          />

          {isCalculatingRoutes && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(13, 17, 23, 0.9)',
              padding: '1rem 2rem',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--accent-primary)',
              border: '1px solid var(--border-default)'
            }}>
              Building routes with traffic data...
            </div>
          )}

          <div style={{
            position: 'absolute',
            bottom: '1rem',
            left: '1rem',
            backgroundColor: 'rgba(13, 17, 23, 0.9)',
            padding: '0.5rem',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.75rem',
            zIndex: 10
          }}>
            <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--accent-primary)' }}>
              Route Legend
            </div>
            {mapRouteKeys.map(driverId => (
              <div key={driverId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <div style={{ width: '20px', height: '4px', backgroundColor: getDriverColor(driverId), borderRadius: '2px' }}></div>
                <span>{getDriverName(driverId)}</span>
              </div>
            ))}
            {mapRouteKeys.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                Select a driver to view routes
              </div>
            )}
          </div>

          {mapRouteKeys.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(13, 17, 23, 0.9)',
              padding: '1.5rem 2rem',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-default)',
              zIndex: 10
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Select drivers to view their routes</p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Routes follow actual roads with live traffic data</p>
            </div>
          )}

          {isDraggingWaypoint && waypointDragInfo && (
            <div style={{
              position: 'absolute',
              top: '1rem',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(241, 196, 15, 0.95)',
              padding: '0.75rem 1.5rem',
              borderRadius: 'var(--radius-md)',
              color: '#000',
              fontWeight: 'bold',
              zIndex: 20,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}>
              Dragging waypoint #{waypointDragInfo.stopIndex + 1} - Release to set new location
            </div>
          )}

          {Object.keys(modifiedStops).length > 0 && (
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              backgroundColor: 'rgba(13, 17, 23, 0.95)',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--accent-primary)',
              zIndex: 15,
              minWidth: '180px'
            }}>
              <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--accent-primary)' }}>
                {Object.keys(modifiedStops).length} waypoint(s) modified
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={saveModifiedWaypoints}
                  disabled={isSavingWaypoints}
                  style={{
                    padding: '0.5rem 0.8rem',
                    backgroundColor: 'var(--accent-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: isSavingWaypoints ? 'wait' : 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    width: '100%',
                    opacity: isSavingWaypoints ? 0.7 : 1
                  }}
                >
                  {isSavingWaypoints ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={resetModifiedStops}
                  disabled={isSavingWaypoints}
                  style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: isSavingWaypoints ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem',
                    width: '100%',
                    opacity: isSavingWaypoints ? 0.5 : 1
                  }}
                >
                  Reset All Changes
                </button>
              </div>
            </div>
          )}

          <div style={{
            position: 'absolute',
            bottom: '1rem',
            right: '1rem',
            backgroundColor: 'rgba(13, 17, 23, 0.9)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            zIndex: 10
          }}>
            Drag markers to adjust waypoint locations
          </div>
        </div>
      );
    }

    if (googleMapsError) {
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'var(--bg-primary)' }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(231, 76, 60, 0.9)',
            padding: '1.5rem 2rem',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            color: 'white',
            maxWidth: '400px'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>{mapsProblem === 'AUTH_FAILED' ? 'Google refused the map key' : 'Google Maps could not load'}</p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem' }}>
              {mapsKeyProblemText(mapsProblem || 'LOAD_FAILED')}
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.75rem', opacity: 0.85 }}>
              Draft routes, Build route and Finalize still work - drive times are estimated until the map key works.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'white',
                color: '#e74c3c',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    const allStops = [];
    const routeLines = [];

    mapRouteKeys.forEach((driverId) => {
      const route = routeFor(driverId);
      if (route?.stopsWithETAs) {
        const color = getDriverColor(driverId);
        const driverStops = route.stopsWithETAs.map((stop, stopIndex) => ({
          ...stop,
          driverId,
          color,
          stopIndex
        }));
        allStops.push(...driverStops);

        if (driverStops.length > 1) {
          routeLines.push({
            driverId,
            color,
            stops: driverStops
          });
        }
      }
    });

    const getDefaultBounds = () => {
      if (config.homeLat && config.homeLng && config.homeRadius) {
        const latDelta = config.homeRadius / 111;
        const lngDelta = config.homeRadius / (111 * Math.cos(config.homeLat * Math.PI / 180));
        return {
          north: config.homeLat + latDelta,
          south: config.homeLat - latDelta,
          east: config.homeLng + lngDelta,
          west: config.homeLng - lngDelta
        };
      }
      return { north: 44.5, south: 43.5, east: -79, west: -80 };
    };

    // __ROUTE_DRAFTS__ fit whatever is drawn (a draft has no Build Routes bounds)
    const stopsBounds = () => {
      const pts = allStops.filter((st) => st.latitude && st.longitude).map((st) => ({ lat: parseFloat(st.latitude), lng: parseFloat(st.longitude) }));
      if (!pts.length) return null;
      const lats = pts.map((x) => x.lat); const lngs = pts.map((x) => x.lng);
      return { north: Math.max(...lats) + 0.1, south: Math.min(...lats) - 0.1, east: Math.max(...lngs) + 0.1, west: Math.min(...lngs) - 0.1 };
    };
    const bounds = mapBounds || stopsBounds() || getDefaultBounds();
    const width = 100;
    const height = 100;

    const latToY = (lat) => {
      return ((bounds.north - lat) / (bounds.north - bounds.south)) * height;
    };

    const lngToX = (lng) => {
      return ((lng - bounds.west) / (bounds.east - bounds.west)) * width;
    };

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'var(--bg-primary)' }}>
        <svg
          ref={mapRef}
          viewBox={`0 0 ${width} ${height}`}
          style={{ 
            width: '100%', 
            height: '100%'
          }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {routeLines.map((route, i) => (
              <marker
                key={`arrow-${i}`}
                id={`arrow-${route.driverId}`}
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0,0 L0,6 L9,3 z" fill={route.color} />
              </marker>
            ))}
          </defs>

          <rect x="0" y="0" width={width} height={height} fill="var(--surface-1)" />
          {[...Array(11)].map((_, i) => (
            <React.Fragment key={`grid-${i}`}>
              <line x1={i * 10} y1="0" x2={i * 10} y2={height} stroke="var(--border-default)" strokeWidth="0.2" opacity="0.5" />
              <line x1="0" y1={i * 10} x2={width} y2={i * 10} stroke="var(--border-default)" strokeWidth="0.2" opacity="0.5" />
            </React.Fragment>
          ))}
          <text x="50" y="50" textAnchor="middle" fill="var(--text-muted)" fontSize="3" opacity="0.5">
            SIMULATION MODE
          </text>

          {routeLines.map((route, routeIndex) => {
            const points = route.stops
              .filter(s => s.latitude && s.longitude)
              .map(s => `${lngToX(parseFloat(s.longitude))},${latToY(parseFloat(s.latitude))}`)
              .join(' ');

            return (
              <polyline
                key={`route-${routeIndex}`}
                points={points}
                fill="none"
                stroke={route.color}
                strokeWidth="0.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                markerEnd={`url(#arrow-${route.driverId})`}
                style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}
              />
            );
          })}

          {allStops.map((stop, index) => {
            if (!stop.latitude || !stop.longitude) return null;
            const x = lngToX(parseFloat(stop.longitude));
            const y = latToY(parseFloat(stop.latitude));
            const isPickup = stop.stop_type === 'pickup';

            return (
              <g key={`stop-${index}`}>
                {isPickup ? (
                  <rect
                    x={x - 1.5}
                    y={y - 1.5}
                    width="3"
                    height="3"
                    fill={stop.color}
                    stroke="#fff"
                    strokeWidth="0.3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedLoad(stop.loadId || stop.load_id)}
                  />
                ) : (
                  <circle
                    cx={x}
                    cy={y}
                    r="1.5"
                    fill={stop.color}
                    stroke="#fff"
                    strokeWidth="0.3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedLoad(stop.loadId || stop.load_id)}
                  />
                )}
                {stop.stopIndex === 0 && (
                  <text
                    x={x + 2}
                    y={y + 0.5}
                    fontSize="2"
                    fill="#fff"
                  >
                    Start
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          backgroundColor: 'rgba(243, 156, 18, 0.9)',
          color: '#000',
          padding: '0.25rem 0.5rem',
          borderRadius: '3px',
          fontSize: '0.7rem'
        }}>
          {mapsProblem === 'NO_KEY' ? mapsKeyProblemText('NO_KEY') : (googleMapsLoaded || !mapsProblem ? 'Loading Google Maps...' : 'Simulated map - Google Maps is not available')}
        </div>

        <div style={{
          position: 'absolute',
          bottom: '1rem',
          left: '1rem',
          backgroundColor: 'rgba(13, 17, 23, 0.9)',
          padding: '0.5rem',
          borderRadius: 'var(--radius-md)',
          fontSize: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <div style={{ width: '10px', height: '10px', border: '2px solid #fff' }}></div>
            <span>Pickup (square)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', border: '2px solid #fff' }}></div>
            <span>Delivery (circle)</span>
          </div>
        </div>

        {allStops.length === 0 && mapRouteKeys.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(13, 17, 23, 0.9)',
            padding: '1.5rem 2rem',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border-default)'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Select drivers to view their routes</p>
            <p style={{ margin: 0, fontSize: '0.85rem' }}>Or drag unassigned jobs to drivers to build routes</p>
          </div>
        )}
      </div>
    );
  };

  const renderJobList = () => {
    const smallBtn = (on) => ({
      padding: '0.3rem 0.55rem', backgroundColor: on ? 'var(--accent-primary)' : 'var(--btn-secondary-bg)',
      color: on ? 'var(--text-primary)' : 'var(--text-secondary)', border: 'none', borderRadius: 'var(--radius-sm)',
      cursor: 'pointer', fontSize: '0.75rem',
    });
    const count = (mode) => (mode === 'all' ? loads.length : mode === 'unassigned' ? loads.filter(l => !l.driver_id).length : loads.filter(l => l.driver_id).length);
    const listIds = listDndIds();
    const shown = new Set(listIds);
    const tile = (id) => {
      const load = loadsById[id];
      if (!load || !shown.has('job:' + id)) return null;
      return (
        <SortableJobTile
          key={'job:' + id}
          id={'job:' + id}
          data={{ type: 'job', loadId: id }}
          load={load}
          {...tileProps(load)}
        />
      );
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{
          padding: '0.75rem',
          borderBottom: '1px solid var(--border-default)',
          backgroundColor: 'var(--surface-1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
            <span style={{ fontWeight: 'bold' }}>Jobs</span>
            <input
              type="date"
              value={routeDay}
              onChange={(e) => setRouteDay(e.target.value || todayLocalISO())}
              title="Route day: which day's routes are shown, and the day new drafts plan for"
              style={{ padding: '0.25rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {['all', 'unassigned', 'assigned'].map(mode => (
              <button key={mode} onClick={() => setFilterMode(mode)} style={{ ...smallBtn(filterMode === mode), textTransform: 'capitalize' }}>
                {mode} ({count(mode)})
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={groupByRoute} onChange={(e) => setGroupByRoute(e.target.checked)} />
              Group by route
            </label>
            {groupByRoute && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }} title="Show the individual jobs inside every route">
                <input type="checkbox" checked={expandAllRoutes} onChange={(e) => setExpandAllRoutes(e.target.checked)} />
                Show jobs inside routes
              </label>
            )}
          </div>
          {selectedIds.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.6rem', padding: '0.4rem', borderRadius: 'var(--radius-sm)', backgroundColor: 'rgba(88,166,255,0.12)', border: '1px solid var(--accent-primary)', fontSize: '0.78rem' }}>
              <span style={{ fontWeight: 'bold' }}>{selectedIds.size} selected</span>
              <button
                onClick={buildRouteFromSelection}
                disabled={draftBusy}
                title="Suggest the best order for the selected jobs (traffic drive times, each job's stop times, pickups before drop-offs) as a new draft route"
                style={{ ...smallBtn(true), fontWeight: 'bold', marginLeft: 'auto', cursor: draftBusy ? 'wait' : 'pointer' }}
              >
                {draftBusy ? 'Building...' : 'Build route'}
              </button>
              {activeDraft && (
                <button onClick={() => { addToDraft(activeDraft.id, listModel.ids.filter((id) => selectedIds.has(id)), null); setSelectedIds(new Set()); }} style={smallBtn(false)} title={'Add to draft "' + activeDraft.name + '"'}>
                  Add to draft
                </button>
              )}
              <button onClick={() => setSelectedIds(new Set())} style={smallBtn(false)}>Clear selection</button>
            </div>
          )}
        </div>

        <SortableContext id="joblist" items={listIds} strategy={verticalListSortingStrategy}>
          <JobListDropArea>
            {listModel.total === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                No jobs found
              </div>
            ) : (
              <>
                {listModel.groups.map((g) => {
                  const open = isRouteOpen(g.route.id);
                  const color = g.route.driver_id ? getDriverColor(g.route.driver_id) : '#95a5a6';
                  return (
                    <div key={g.route.id} style={{ marginBottom: '0.6rem', border: `1px solid ${color}`, borderRadius: 'var(--radius-md)', padding: '0.45rem', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: open ? '0.45rem' : 0 }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: color, flexShrink: 0 }} />
                        <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.route.route_number || ''}>
                          {g.route.driver_id ? getDriverName(g.route.driver_id) : 'Unassigned route'}
                        </span>
                        <span style={{ color: 'var(--text-muted)' }}>{g.loadIds.length} job(s)</span>
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
                          <button onClick={() => routeToDraft(g.route)} style={smallBtn(false)} title="Copy this route's jobs into a new draft to re-plan them (the real route stays until you finalize)">To draft</button>
                          {!expandAllRoutes && (
                            <button onClick={() => toggleRouteOpen(g.route.id)} style={smallBtn(open)}>{open ? 'Hide jobs' : 'Show jobs'}</button>
                          )}
                        </span>
                      </div>
                      {open && g.loadIds.map(tile)}
                    </div>
                  );
                })}
                {listModel.groups.length > 0 && listModel.others.length > 0 && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.2rem 0 0.4rem' }}>Not on a route for {routeDay}</div>
                )}
                {listModel.others.map(tile)}
              </>
            )}
          </JobListDropArea>
        </SortableContext>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '0.4rem 0.75rem', borderTop: '1px solid var(--border-default)' }}>
          Click tiles to select. Drag to re-order, into a draft, or onto a driver to assign.
        </div>
      </div>
    );
  };

  const renderDriverPanel = () => {
    return (
      <div style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-default)',
        backgroundColor: 'var(--surface-1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 'bold' }}>Drivers on Map</div>
          <button
            onClick={handleBuildRoutes}
            disabled={isCalculatingRoutes}
            style={{
              padding: '0.4rem 0.75rem',
              backgroundColor: isCalculatingRoutes ? 'var(--btn-secondary-bg)' : 'var(--accent-primary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: isCalculatingRoutes ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}
          >
            {isCalculatingRoutes ? 'Building...' : 'Build Routes'}
          </button>
        </div>
        {selectedEquipLoads.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
            Equipment for the {selectedEquipLoads.length} selected job(s): OK fits, ! check, X incompatible (hover a driver for why).
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {drivers.map(driver => {
            const isSelected = selectedDrivers.includes(driver.id);
            const color = getDriverColor(driver.id);
            const route = routeData[driver.id];
            const hasViolations = timeViolations.some(v => v.driverId === driver.id);
            const eqHint = selectedEquipLoads.length ? quickHint({ driverId: driver.id, loads: selectedEquipLoads, fleet: equipFleet, rules: equipRules }) : null;

            return (
              <DriverDropChip
                key={driver.id}
                driverId={driver.id}
                onClick={() => toggleDriverSelection(driver.id)}
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: isSelected ? color : 'var(--btn-secondary-bg)',
                  color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  border: `2px solid ${hasViolations ? 'var(--color-danger)' : color}`,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                <div style={{ fontWeight: 'bold' }} title={eqHint ? eqHint.title : undefined}>
                  {driver.name || driver.email || 'Driver'}
                  {eqHint && (
                    <span data-equip-badge={eqHint.level} style={{ marginLeft: '0.35rem', padding: '0 0.3rem', borderRadius: '3px', fontSize: '0.68rem',
                      backgroundColor: eqHint.level === 'block' ? 'var(--color-danger)' : eqHint.level === 'warn' ? 'var(--color-warning)' : 'var(--color-success)', color: 'var(--text-primary)' }}>
                      {eqHint.level === 'block' ? 'X' : eqHint.level === 'warn' ? '!' : 'OK'}
                    </span>
                  )}
                </div>
                {route && (
                  <div style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                    {route.stopsWithETAs?.length || 0} stops
                  </div>
                )}
                {hasViolations && (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    backgroundColor: 'var(--color-danger)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>!</span>
                )}
              </DriverDropChip>
            );
          })}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          Click to show/hide routes. Drag a job onto a driver to assign it now.
        </div>
      </div>
    );
  };

  const renderDriverSelectPrompt = () => {
    if (!showDriverSelectPrompt) return null;

    const driversWithLoads = drivers.filter(d => 
      loads.some(l => l.driver_id === d.id)
    );

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'var(--surface-1)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          maxWidth: '400px',
          width: '90%',
          border: '1px solid var(--border-default)'
        }}>
          <h3 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>
            Select a Driver to Route
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Please select a single driver to build their route. This ensures accurate routing calculations.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
            {driversWithLoads.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
                No drivers have assigned loads. Assign loads to drivers first.
              </div>
            ) : (
              driversWithLoads.map(driver => (
                <button
                  key={driver.id}
                  onClick={() => selectSingleDriverAndBuildRoute(driver.id)}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--btn-secondary-bg)',
                    color: 'var(--text-primary)',
                    border: `2px solid ${getDriverColor(driver.id)}`,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>{driver.name || driver.email || 'Driver'}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {loads.filter(l => l.driver_id === driver.id).length} loads
                  </span>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => setShowDriverSelectPrompt(false)}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#7f8c8d',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderTimeViolations = () => {
    if (timeViolations.length === 0) return null;

    return (
      <div style={{
        position: 'absolute',
        top: '0.5rem',
        right: '0.5rem',
        backgroundColor: 'rgba(231, 76, 60, 0.95)',
        padding: '0.75rem',
        borderRadius: 'var(--radius-md)',
        maxWidth: '300px',
        maxHeight: '200px',
        overflow: 'auto',
        fontSize: '0.8rem',
        border: '1px solid var(--color-danger)',
        zIndex: 100
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>Time Constraint Violations</span>
          <button
            onClick={() => setTimeViolations([])}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            x
          </button>
        </div>
        {timeViolations.map((violation, index) => (
          <div key={index} style={{ marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: index < timeViolations.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>
            <div style={{ fontWeight: 'bold' }}>{violation.driverName}</div>
            <div style={{ fontSize: '0.75rem' }}>
              {violation.stop.address?.split(',')[0]} - {violation.message}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderReassignWarning = () => {
    if (!showReassignWarning) return null;

    const { load, sourceDriverId, targetDriverId, compatibility, impact } = showReassignWarning;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'var(--surface-1)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          border: '1px solid var(--border-default)'
        }}>
          <h3 style={{ marginTop: 0, color: 'var(--color-warning)' }}>
            Reassign Load #{load.load_number || load.id.slice(0, 8)}?
          </h3>

          {compatibility && (compatibility.issues.length > 0 || compatibility.unchecked) && (
            <div style={{
              backgroundColor: compatibility.canAssign && !compatibility.needsReason ? 'rgba(243, 156, 18, 0.15)' : 'rgba(231, 76, 60, 0.2)',
              padding: '0.75rem',
              borderRadius: '5px',
              marginBottom: '1rem',
              borderLeft: '4px solid ' + (compatibility.canAssign && !compatibility.needsReason ? 'var(--color-warning)' : 'var(--color-danger)')
            }}>
              <div style={{ fontWeight: 'bold', color: compatibility.canAssign && !compatibility.needsReason ? 'var(--color-warning)' : 'var(--color-danger)', marginBottom: '0.5rem' }}>
                {!compatibility.canAssign ? 'Cannot assign (driver or unit no longer exists)' : compatibility.needsReason ? 'Equipment warning (Confirm asks for a reason)' : compatibility.ok ? 'Equipment - notes' : 'Warnings (Confirm shows them once more; reason optional)'}
              </div>
              <EquipmentIssueList result={compatibility} compact />
            </div>
          )}

          {impact?.criticalViolations?.length > 0 && (
            <div style={{
              backgroundColor: 'rgba(231, 76, 60, 0.2)',
              padding: '0.75rem',
              borderRadius: '5px',
              marginBottom: '1rem',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              <div style={{ fontWeight: 'bold', color: 'var(--color-danger)', marginBottom: '0.5rem' }}>
                Critical Time Violations
              </div>
              {impact.criticalViolations.map((violation, i) => (
                <div key={i} style={{ fontSize: '0.85rem', color: 'var(--color-danger)' }}>
                  Will arrive {violation.lateBy} min late at {violation.stop.address?.split(',')[0]}
                </div>
              ))}
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {impact?.sourceDriver && (
              <div style={{
                backgroundColor: 'var(--surface-1)',
                padding: '0.75rem',
                borderRadius: '5px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  From: {impact.sourceDriver.name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#2ecc71' }}>
                  {impact.sourceDriver.currentStops} → {impact.sourceDriver.newStops} stops
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  ~{impact.sourceDriver.timeSaved} min freed up
                </div>
              </div>
            )}

            {impact?.targetDriver && (
              <div style={{
                backgroundColor: 'var(--surface-1)',
                padding: '0.75rem',
                borderRadius: '5px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  To: {impact.targetDriver.name}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-warning)' }}>
                  {impact.targetDriver.currentStops} → {impact.targetDriver.newStops} stops
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  ~{impact.targetDriver.timeAdded} min added
                </div>
              </div>
            )}
          </div>

          {/* __DRAG_PREVIEW_REAL__ say where the numbers came from */}
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            {impact?.error
              ? 'Could not re-plan the routes (' + impact.error + '); stop counts and times are not shown.'
              : 'Times come from re-planning both drivers\u2019 full day with and without this job. Nothing is saved until you confirm. Swap Preview shows every stop.'}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowReassignWarning(null);
                setDraggedLoad(null);
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#7f8c8d',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmReassignment}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: impact?.criticalViolations?.length > 0 ? 'var(--color-danger)' : 'var(--accent-primary)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {impact?.criticalViolations?.length > 0 ? 'Assign Anyway' : 'Confirm Assignment'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddressWarnings = () => {
    if (addressWarnings.length === 0) return null;
    return (
      <div style={{
        position: 'absolute',
        bottom: '0.5rem',
        left: '0.5rem',
        backgroundColor: 'rgba(180, 60, 30, 0.95)',
        padding: '0.75rem',
        borderRadius: 'var(--radius-md)',
        maxWidth: '350px',
        maxHeight: '200px',
        overflow: 'auto',
        fontSize: '0.8rem',
        border: '2px solid var(--color-danger)',
        zIndex: 10
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>⚠️ {addressWarnings.length} Address(es) Failed</span>
          <button
            onClick={() => setAddressWarnings([])}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem' }}
          >×</button>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem', fontSize: '0.75rem' }}>
          These stops were skipped from the route. Fix the address in the load details.
        </div>
        {addressWarnings.map((w, i) => (
          <div key={i} style={{
            padding: '0.4rem',
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '3px',
            marginBottom: '0.25rem',
            fontSize: '0.75rem'
          }}>
            <div style={{ fontWeight: 'bold' }}>{w.loadNumber} — {w.stopType}</div>
            <div style={{ color: 'rgba(255,255,255,0.7)' }}>{w.address}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderRouteDetails = () => {
    if (selectedDrivers.length === 0) return null;

    return (
      <div style={{
        position: 'absolute',
        top: '0.5rem',
        left: '0.5rem',
        backgroundColor: 'rgba(13, 17, 23, 0.95)',
        padding: '0.75rem',
        borderRadius: 'var(--radius-md)',
        maxWidth: '250px',
        maxHeight: '300px',
        overflow: 'auto',
        fontSize: '0.8rem',
        border: '1px solid var(--border-default)'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Route Details</div>
        {selectedDrivers.map(driverId => {
          const route = routeData[driverId];
          const color = getDriverColor(driverId);

          return (
            <div key={driverId} style={{ marginBottom: '0.75rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  backgroundColor: color,
                  borderRadius: '2px'
                }}></div>
                <span style={{ fontWeight: 'bold' }}>{getDriverName(driverId)}</span>
              </div>
              {route ? (
                <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                  <div>{route.stopsWithETAs?.length || 0} stops</div>
                  {route.totalDistanceKm && (
                    <div>{Math.round(route.totalDistanceKm)} km</div>
                  )}
                  {route.totalDriveTime && (
                    <div>Drive: {route.totalDriveTime}</div>
                  )}
                  {route.totalDwellTime && (
                    <div>Dwell: {route.totalDwellTime}</div>
                  )}
                  {route.estimatedEndTime && (
                    <div>Done by {new Date(route.estimatedEndTime).toLocaleTimeString()}</div>
                  )}
                  {route.isOptimized && (
                    <div style={{ color: 'var(--accent-primary)' }}>✓ Route optimized</div>
                  )}
                </div>
              ) : (
                <div style={{ paddingLeft: '1rem', color: 'var(--text-muted)' }}>No route data</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'var(--text-secondary)'
      }}>
        Loading route builder...
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionStrategy}
      onDragStart={handleDndStart}
      onDragOver={handleDndOver}
      onDragEnd={handleDndEnd}
      onDragCancel={handleDndCancel}
    >
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      backgroundColor: 'var(--bg-primary)'
    }}>
      {routeBuilderStatus && (
        <div style={{
          padding: '0.6rem 1rem',
          backgroundColor: routeBuilderStatus.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
          color: 'var(--text-primary)',
          fontSize: '0.85rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <span>{routeBuilderStatus.text}</span>
          <button onClick={() => setRouteBuilderStatus(null)} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>
      )}
      {renderDriverPanel()}

      <div style={{ 
        display: 'flex', 
        flex: 1,
        overflow: 'hidden'
      }}>
        <div style={{ 
          width: '320px', 
          borderRight: '1px solid var(--border-default)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {renderJobList()}
        </div>

        <div style={{
          width: '320px',
          borderRight: '1px solid var(--border-default)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bg-primary)'
        }}>
          {renderDraftsColumn()}
        </div>

        <div style={{ 
          flex: 1, 
          position: 'relative'
        }}>
          {renderMap()}
          {renderRouteDetails()}
          {renderAddressWarnings()}
          {renderTimeViolations()}
        </div>
      </div>

      {renderReassignWarning()}
      {renderDriverSelectPrompt()}
      {equipmentModal}
      {finalizeFor && (
        <FinalizeDialog
          draft={finalizeFor}
          drivers={drivers.filter((d) => d.is_available !== false)}
          getDriverName={getDriverName}
          onPreview={(targetDriverId, date) => previewFinalize({ loadIds: finalizeFor.loadIds, targetDriverId, date })}
          onQualify={async (date) => {
            // __EQUIP_V2__ who qualifies for the whole route on that day vs who does not (equipment,
            // restrictions, half-load season, his day / HOS) - Henry picks either way
            const full = await fetchFullLoads(finalizeFor.loadIds);
            const added = timelineTime(await routeTimelineFor(finalizeFor, full, date)) || undefined;
            return qualifyDrivers({ drivers: drivers.filter((d) => d.is_available !== false), loads: full,
              stopIds: draftStopOrder(full, finalizeFor.stop_order), date, added });
          }}
          onConfirm={handleFinalizeConfirm}
          onCancel={() => setFinalizeFor(null)}
        />
      )}
    </div>
    <DragOverlay dropAnimation={dropAnimation}>{renderDragOverlay()}</DragOverlay>
    </DndContext>
  );
}

export default RouteBuilder;
